import { useEffect, useRef, useState } from "react";
import axios from "axios";
import BACKEND_URL from "../lib/apiConfig";

const API = `${BACKEND_URL}/api`;

export function useUnreadBulletins(userId) {
  const [state, setState] = useState({ userId: null, items: [] });
  const [ack, setAck] = useState({ pending: false, error: "", id: null });
  const confirmed = useRef(new Set());
  const locked = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    const version = ++generation.current;
    confirmed.current = new Set();
    locked.current = false;
    setState({ userId, items: [] });
    setAck({ pending: false, error: "", id: null });
    if (!userId) return;
    let stopped = false;
    let fetching = false;
    const fetchUnread = async () => {
      if (fetching) return;
      fetching = true;
      try {
        const res = await axios.get(`${API}/bulletins/unread/${userId}`);
        if (!stopped && version === generation.current) {
          setState({ userId, items: (res.data || []).filter(b => !confirmed.current.has(b.id)) });
        }
      } catch { /* Retain the current bulletin during a failed background poll. */ }
      finally { fetching = false; }
    };
    fetchUnread();
    const interval = setInterval(() => { if (!document.hidden) fetchUnread(); }, 30000);
    return () => { stopped = true; generation.current += 1; clearInterval(interval); };
  }, [userId]);

  const unread = state.userId === userId ? state.items : [];
  const current = unread[0];
  const acknowledge = async () => {
    if (!current || locked.current) return;
    const id = current.id;
    const version = generation.current;
    locked.current = true;
    setAck({ pending: true, error: "", id });
    try {
      await axios.post(`${API}/bulletins/${id}/acknowledge?user_id=${encodeURIComponent(userId)}`);
      if (version !== generation.current) return;
      confirmed.current.add(id);
      setState(prev => ({ ...prev, items: prev.items.filter(b => b.id !== id) }));
      window.dispatchEvent(new CustomEvent("bulletin-acknowledged", { detail: { userId, bulletinId: id } }));
    } catch {
      if (version === generation.current) {
        setAck({ pending: false, error: "Could not acknowledge this bulletin. Please try again.", id });
      }
    } finally {
      if (version === generation.current) {
        locked.current = false;
        setAck(prev => ({ ...prev, pending: false }));
      }
    }
  };
  return { current, count: unread.length, acknowledge, acknowledging: ack.pending,
    error: ack.id === current?.id ? ack.error : "" };
}